import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MdDeleteOutline, MdTrendingUp, MdTrendingDown, MdLayers, MdDateRange, MdInfoOutline } from 'react-icons/md';
import { BiTargetLock } from 'react-icons/bi';
import { FiRepeat } from 'react-icons/fi';
import { CURRENCY_CONFIG } from '../../class/GlobalVars';
import TickerPrice from '../../components/common/TickerPrice';
import { Badge } from '../../components/ds';

const WatchListRow = ({ inst, previousClose, onRemoveInstrument, onViewProfile }) => {
  const navigate = useNavigate();

  const handleRowClick = (e) => {
    // Prevent navigation if clicking delete or info buttons
    if (e.target.closest('button')) return;
    
    navigate('/SmartTrade', { state: { instrument: inst } });
  };

  // Helper: Format Date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  // Helper: Get Flag
  const getCountryFlag = (countryCode) => {
    if (!countryCode) return null;
    if (countryCode === 'ALL') return null;
    let code = countryCode.toLowerCase();
    if (code === 'uk') code = 'gb';
    return `https://flagcdn.com/w40/${code}.png`;
  };

  // Color logic: Use previousClose if available, otherwise fallback to change_percent
  // When previousClose is provided: green if LTP >= previousClose, red if below
  const isPositive = previousClose && inst.ltp != null
    ? inst.ltp >= previousClose
    : inst.change_percent >= 0;
  
  // Determine display value and styling
  const hasLtp = inst.ltp != null;
  const displayValue = hasLtp ? inst.ltp : previousClose;
  
  // If we have LTP, use status color (Green/Red). 
  // If no LTP but showing Previous Close, use neutral primary text (text-fg-1).
  // If neither, fallback to idle color.
  const statusColor = hasLtp
    ? (isPositive ? 'text-profit' : 'text-loss')
    : 'text-fg-1';

  // Resolved hex for the TickerPrice color tween (framer-motion needs a real
  // color value to interpolate; these equal DS --color-profit / --color-loss,
  // which are theme-invariant, so no dark-mode hardcode).
  const baseColorHex = hasLtp
    ? (isPositive ? '#00C292' : '#E46A76')
    : undefined;

  const gradientBg = hasLtp && isPositive
    ? 'from-profit/5 to-transparent'
    : (hasLtp ? 'from-loss/5 to-transparent' : 'from-fg-3/5 to-transparent');

  return (
    <div 
      onClick={handleRowClick}
      className={`group relative grid grid-cols-1 md:grid-cols-12 gap-y-4 md:gap-x-4 items-center p-5 bg-bg-2 rounded-[24px] shadow-sm hover:shadow-2xl hover:scale-[1.01] transition-all duration-300 overflow-hidden isolate cursor-pointer`}
    >
      {/* Subtle Gradient Backdrop */}
      <div className={`absolute inset-0 bg-gradient-to-r ${gradientBg} md:bg-gradient-to-l opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10`} />

      {/* Col 1: Identity (4 cols) */}
      <div className="md:col-span-4 flex items-center gap-5 w-full">
        <div className={`h-14 w-14 rounded-2xl shrink-0 flex items-center justify-center bg-bg-3 group-hover:bg-bg-2 transition-colors shadow-inner relative`}>
           <span className="text-xl font-black text-fg-3">
             {inst.symbol.charAt(0)}
           </span>
           {/* Country Flag Badge */}
           <div className="absolute -bottom-1 -right-1 bg-bg-2 border border-[var(--border)] rounded-full h-5 w-5 flex items-center justify-center shadow-sm z-10 overflow-hidden">
               <img 
                 src={getCountryFlag(inst.country_code)}
                 alt={inst.country_code}
                 className="w-full h-full object-cover rounded-full"
                 onError={(e) => e.target.style.display = 'none'}
               />
           </div>
        </div>

        <div className="flex flex-col min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-lg font-black tracking-tight text-fg-1 truncate">
              {inst.symbol}
            </h3>

            {/* Continuous Badge */}
            {inst.is_continuous && (
              <Badge variant="role" size="xs" className="shrink-0">
                <FiRepeat size={10} /> CONT.
              </Badge>
            )}

            <Badge
              variant={inst.exchange === 'NSE' ? 'info' : 'warn'}
              size="xs"
              className="shrink-0 uppercase tracking-wider"
            >
              {inst.exchange}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-fg-3 uppercase tracking-wide truncate">
                {inst.name || inst.description}
            </span>
            {previousClose && !hasLtp && (
                <span className="text-[10px] font-bold text-fg-3 italic bg-bg-3 px-1.5 py-0.5 rounded">Prev. Close</span>
            )}
          </div>
        </div>
      </div>

      {/* Col 2: Details (3 cols) */}
      <div className="md:col-span-3 h-full flex flex-col justify-center pl-19 md:pl-0 border-l md:border-l-0 border-dashed border-[var(--border)] ml-4 md:ml-0 md:border-r md:border-[var(--divider)]">
         <div className="grid grid-cols-2 gap-x-4 gap-y-3 w-full pr-4">
            
            {/* LEFT COLUMN: Type & Strike */}
            <div className="flex flex-col gap-1 items-start">
               {/* Instrument Type */}
               <div className="flex items-center gap-1.5">
                  <MdLayers className="text-fg-3 text-xs shrink-0" />
                  <span className="text-[10px] font-bold text-fg-2 uppercase tracking-wide">
                     {inst.instrument_type || 'EQUITY'}
                  </span>
               </div>

               {/* Strike Price */}
               {(inst.option_type || inst.strike_price) ? (
                   <div className="flex items-center gap-1.5">
                      <BiTargetLock className="text-[var(--theme-purple)] text-xs shrink-0" />
                      <span className="text-xs font-black text-[var(--theme-purple)]">
                         {inst.strike_price} {inst.option_type}
                      </span>
                   </div>
               ) : (
                  <div className="h-4"></div>
               )}
            </div>

            {/* RIGHT COLUMN: Dates */}
            <div className="flex flex-col gap-1 items-start">
                {/* Expiry */}
                {(inst.expiry_date || inst.active_expiry_date || inst.instrument_type !== 'EQUITY') && (
                    <div className="flex items-center gap-1.5" title="Expiry Date">
                       <MdDateRange className="text-fg-3 text-xs shrink-0" />
                       <span className="text-[10px] font-bold text-fg-1">
                          {/* For continuous contracts, ALWAYS prioritize the resolved expiry_date */}
                          {formatDate(inst.is_continuous ? inst.expiry_date : (inst.active_expiry_date || inst.expiry_date))}
                       </span>
                    </div>
                )}

               {/* Rollover Date (if continuous) */}
               {inst.is_continuous && (
                  <div className="flex items-center gap-1.5 text-[var(--theme-purple)]" title="Next Rollover Date">
                     <FiRepeat className="text-xs shrink-0" />
                     <span className="text-[10px] font-bold whitespace-nowrap">
                        Roll: {inst.next_rollover_date ? formatDate(inst.next_rollover_date) : `Auto (${inst.rollover_days_before_expiry || 0}d)`}
                     </span>
                  </div>
               )}
            </div>

         </div>
      </div>

      {/* Col 3: Algo Stats (2 cols) */}
      <div className="md:col-span-2 flex items-center md:justify-center pl-19 md:pl-0">
         <div className="flex flex-col items-start md:items-center gap-1">
            <span className="text-[10px] font-bold text-fg-3 uppercase tracking-widest opacity-60">Algo Status</span>
            {inst.strategies_count > 0 ? (
              <div className="flex items-center gap-2">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[var(--theme-purple)] opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-[var(--theme-purple)]"></span>
                </span>
                <span className="text-sm font-bold text-[var(--theme-purple)]">
                  {inst.strategies_count} Active
                </span>
              </div>
            ) : (
              <span className="text-sm font-bold text-fg-3">
                Idle
              </span>
            )}
         </div>
      </div>

      {/* Col 4: Price & Actions (3 cols) */}
      <div className="md:col-span-3 flex items-center justify-between md:justify-end gap-6 pl-19 md:pl-0">
         <div className="flex flex-col items-end">
            <TickerPrice 
               value={displayValue} 
               currency={inst.currency}
               className={`text-2xl font-black ${statusColor}`}
               baseColor={baseColorHex}
            />
            {hasLtp ? (
                <div className={`flex items-center gap-1 font-bold text-xs ${statusColor} bg-opacity-10 px-2 py-0.5 rounded-full`}>
                {isPositive ? <MdTrendingUp /> : <MdTrendingDown />}
                {Math.abs(inst.change_percent).toFixed(2)}%
                </div>
            ) : previousClose ? (
                <div className="flex items-center gap-1 font-bold text-xs text-fg-3 bg-bg-3 px-2 py-0.5 rounded-full">
                    <MdTrendingUp />
                    <span>--%</span>
                </div>
            ) : null}
         </div>

           <button
            onClick={() => onViewProfile(inst)}
            title="View Company Profile"
            className="h-10 w-10 shrink-0 flex items-center justify-center rounded-xl bg-bg-3 text-fg-3 hover:bg-primary hover:text-white transition-all shadow-sm"
           >
            <MdInfoOutline className="text-lg" />
           </button>

           <button
            onClick={() => onRemoveInstrument(inst.instrument_id)}
            className="h-10 w-10 shrink-0 flex items-center justify-center rounded-xl bg-bg-3 text-fg-3 hover:bg-danger hover:text-white transition-all shadow-sm"
           >
            <MdDeleteOutline className="text-lg" />
           </button>
      </div>
    </div>
  );
};

export default WatchListRow;
